/**
 * AltaiLabs brand tokens — shared by WorkspaceCore and any component
 * that renders inside the chat shell (FormCard, ConfirmationCard's
 * DocumentCreatedCard, inline block renderers, etc.).
 *
 * Single source of truth so new surfaces can read `T.border` /
 * `pal.primary` instead of copying hex strings. Kept in sync with the
 * brand book at apps/web/src/pages/Landing.tsx `/brand`.
 */

export const T = {
  white: '#FFFFFF',
  bg: '#FAFAF9',
  surface: '#F5F5F4',
  border: '#E7E5E4',
  borderStrong: '#D6D3D1',
  ink: '#0C0A09',
  text: '#1C1917',
  muted: '#57534E',
  subtle: '#78716C',
  faint: '#A8A29E',
  pale: '#D6D3D1',
  // Error red — the brand-book error state (#C44141, frontend.md), used
  // sparingly and never for decoration. Bg/border are its own tints so
  // every danger surface derives from the same hue.
  danger: '#C44141',
  dangerBg: 'rgba(196,65,65,0.08)',
  dangerBorder: 'rgba(196,65,65,0.3)',
  warn: '#A16207',
  warnBg: '#FEFCE8',
  warnBorder: '#FDE68A',
  success: '#166534',
  successBg: '#F0FDF4',
  successBorder: '#BBF7D0',
} as const;

/** Galter palette — forest green. Used on chat, tool cards, accent. */
export const G = {
  primary: '#0F3D2E',
  deep: '#072218',
  mid: '#1F5C46',
  soft: '#7AAA94',
  pale: '#D6E5DD',
  cream: '#F1F0E5',
} as const;

/** Pacioli palette — ochre. Used for the tax-consultant surface. */
export const P = {
  primary: '#A8732C',
  deep: '#6B4615',
  mid: '#C9954A',
  soft: '#E2BC7E',
  pale: '#F3DEB6',
  cream: '#FBF5E6',
} as const;

export const SERIF = "'Newsreader', Georgia, serif";
export const SANS = "'Manrope', -apple-system, sans-serif";
export const MONO = "'JetBrains Mono', 'SF Mono', Menlo, monospace";

export type BrandPalette = typeof G | typeof P;
export type BrandTokens = typeof T;
